/*
 * Switches currency in place, without a page reload.
 *
 * Progressive enhancement over snippets/currency-selector.liquid: without this file
 * the selector is a plain form that posts and reloads. With it, the same POST goes
 * through fetch (so Shopify still sets the country on the session and the cart), and
 * only the elements that show money are replaced from the re-rendered page. Scroll
 * position, open <details>, and anything else on the page stay as they were.
 *
 * Correctness over smoothness: if the fresh page does not have the same number of
 * money elements as the current one, reload (the country is already set by then);
 * if the request itself fails, fall back to the plain form submit.
 * A half-swapped page would show two currencies at once.
 */
(() => {
  // Every element whose text is rendered with a `money` filter, plus the selector
  // itself (its current-state and target countries change with the currency).
  const SWAP_SELECTORS = [
    '.currency-selector',
    '.product__price',
    '#product-variant',
    '.product-card__price',
    '.ba-showcase__panel-price',
    '.cart__item-price',
    '.cart__subtotal',
    '.search-result__content',
  ];

  function swapFrom(doc) {
    const pairs = [];

    for (const selector of SWAP_SELECTORS) {
      const current = document.querySelectorAll(selector);
      const fresh = doc.querySelectorAll(selector);
      if (current.length !== fresh.length) return false;
      current.forEach((node, i) => pairs.push([node, fresh[i]]));
    }

    for (const [node, fresh] of pairs) {
      // Keep the chosen variant: the fresh page renders the default selection.
      if (node instanceof HTMLSelectElement) fresh.value = node.value;
      node.replaceWith(document.importNode(fresh, true));
    }
    return true;
  }

  // Each currency's version of this page, once seen, keyed by country code. The first
  // switch to a currency has to ask Shopify for it (~1.3s); every switch after that
  // swaps instantly from here while the POST still runs, so the session and the cart
  // still change currency.
  const pages = new Map();
  let busy = false;
  let queued = null; // the last button clicked while a switch was running

  function currentOption() {
    return document.querySelector('.currency-selector__option[aria-current="true"]');
  }

  // Answer the click before the network does: without this the page sat unchanged for
  // over a second, it read as a dead button, and a second click raced the first
  // (reported 2026-10-02).
  function showPending(form, submitter) {
    for (const option of form.querySelectorAll('.currency-selector__option')) {
      option.toggleAttribute('aria-current', option === submitter);
      if (option === submitter) option.setAttribute('aria-current', 'true');
    }
    // Fade the old prices. Set inline because the price elements belong to other
    // sections; the swapped-in replacements arrive without this style.
    for (const selector of SWAP_SELECTORS.slice(1)) {
      for (const node of document.querySelectorAll(selector)) {
        node.style.transition = 'opacity 0.15s ease';
        node.style.opacity = '0.35';
      }
    }
    form.setAttribute('aria-busy', 'true');
  }

  function clearPending() {
    for (const selector of SWAP_SELECTORS.slice(1)) {
      for (const node of document.querySelectorAll(selector)) node.style.opacity = '';
    }
    document.querySelector('.currency-selector')?.removeAttribute('aria-busy');
  }

  async function postLocalization(form, submitter) {
    const response = await fetch(form.action, {
      method: 'POST',
      body: new FormData(form, submitter),
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`localization ${response.status}`);
    return response;
  }

  // Every POST goes through one queue, one at a time. Fired in parallel, quick toggles
  // reached Shopify out of order and the cart ended in a different currency from the
  // one on screen (measured 2026-10-02: screen ¥, cart USD). Background syncs also
  // skip themselves if a later click has superseded them, so only the last one counts.
  let chain = Promise.resolve();
  let wanted = null;

  function enqueue(task) {
    const run = chain.then(task, task);
    chain = run.catch(() => {});
    return run;
  }

  function syncInBackground(form, submitter) {
    const country = submitter.value;
    wanted = country;
    enqueue(async () => {
      if (wanted !== country) return;
      await postLocalization(form, submitter);
    }).catch(() => location.reload());
  }

  async function switchCurrency(form, submitter) {
    const country = submitter.value;
    const from = currentOption();
    if (from && !pages.has(from.value)) pages.set(from.value, document.cloneNode(true));

    showPending(form, submitter);

    const cached = pages.get(country);
    if (cached && swapFrom(cached)) {
      clearPending();
      // The display is already right; this only moves the session and cart over.
      // If it fails, reload so the page shows what checkout will actually charge.
      syncInBackground(form, submitter);
      return;
    }

    wanted = country;
    const response = await enqueue(() => postLocalization(form, submitter));

    // Shopify redirects back to the page after the POST; if it sends us somewhere
    // else (another locale path), follow it properly instead of patching this page.
    const landed = new URL(response.url);
    if (landed.pathname !== location.pathname) {
      location.assign(landed.href);
      return;
    }

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (!swapFrom(doc)) {
      location.reload();
      return;
    }
    pages.set(country, doc);
    clearPending();
  }

  // Any failure hands the click back to the browser as an ordinary form submit, which
  // is exactly what the page did before this file existed.
  function submitNatively(form, submitter) {
    form.dataset.nativeSubmit = 'true';
    form.requestSubmit(submitter);
  }

  async function run(form, submitter) {
    busy = true;
    try {
      await switchCurrency(form, submitter);
    } catch {
      submitNatively(form, submitter);
      return;
    } finally {
      busy = false;
    }

    // A click made while we were busy wins, if it asks for something else.
    const next = queued;
    queued = null;
    const liveForm = document.querySelector('.currency-selector');
    const liveButton = next && liveForm?.querySelector(`.currency-selector__option[value="${next}"]`);
    if (liveButton && liveButton.getAttribute('aria-current') !== 'true') run(liveForm, liveButton);
    else currentOption()?.focus();
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('currency-selector')) return;
    if (form.dataset.nativeSubmit === 'true') return;

    event.preventDefault();
    const submitter = event.submitter;
    if (!submitter) return;

    if (busy) {
      queued = submitter.value;
      return;
    }
    if (submitter.getAttribute('aria-current') === 'true') return;

    run(form, submitter);
  });
})();
